/** What reviewing a person means: the queue their case sits in, and the call that can be logged with it. Pure, so the tests can pin it. */
import { STATE_LICENSE_QUEUE_KEY } from "../../api/stateLicense.types";
import { VIEW } from "../data/queues";

export const OUTCOMES = ["Spoke", "Voicemail", "No answer"] as const;

/** A call being logged: the outcome picked, and the note beside it. Nothing is written until it is saved, or rides along with a review. */
export interface CallDraft {
  outcome: (typeof OUTCOMES)[number] | null;
  note: string;
}

export const NO_CALL: CallDraft = { outcome: null, note: "" };

/** The outreach entry's summary: the outcome, then the note. */
export const callSummary = ({ outcome, note }: CallDraft): string => (note.trim() ? `${outcome}: ${note.trim()}` : (outcome ?? ""));

/** The cases staff work. Held for review is read, not worked: nothing writes to it. */
export type CaseKind = "payment" | "grace" | "suspended" | "licence" | "held";

/** The queue an acknowledgement is keyed on, for every case that can be reviewed. */
export const QUEUE_KEY: Record<Exclude<CaseKind, "held">, string> = {
  payment: "payment_issues",
  grace: "grace",
  suspended: "suspended",
  licence: STATE_LICENSE_QUEUE_KEY,
};

/** The tick that closes a case, in the queue's own words: a licence change is done in CE21 (by hand: CE21 is read-only here), the rest are reviewed. */
export const doneWords = (kind: CaseKind | null): { button: string; toast: string; badge: string } =>
  kind === "licence" ? { button: "Done in CE21", toast: "Done in CE21", badge: "Done in CE21" } : { button: "Mark reviewed", toast: "Marked reviewed", badge: "Reviewed" };

const VIEW_CASE: Record<string, CaseKind> = {
  [VIEW.paymentIssues]: "payment",
  [VIEW.lapsed]: "grace",
  [VIEW.lapsed65]: "grace",
  [VIEW.lapsed120]: "grace",
  [VIEW.suspended]: "suspended",
  [VIEW.licenceChanges]: "licence",
  [VIEW.held]: "held",
};

/**
 * The case a person is in: the queue's own view when the sheet is opened over one, else what their
 * standing says (suspended and lapsed members, or anyone with a payment failing). Null when no case
 * is known yet; licence changes and held people are found in their own lists, not on the person.
 */
export function caseKindOf(viewId: string, standing: string, paymentFailing: boolean): CaseKind | null {
  return VIEW_CASE[viewId] ?? (standing === "suspended" ? "suspended" : standing === "lapsed" ? "grace" : paymentFailing ? "payment" : null);
}
